import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { ICuenta } from '../../models/cuenta/ICuenta.interface';
import { IHttpResponse } from '../../../../models/IHttpResponse.interface';
import { ILoginRequest } from '../../models/ILoginRequest.interface';
import { ITokenResponse } from '../../models/ITokenResponse.interface';
import { IMenu } from '../../models/IMenu.interface';

@Injectable({
  providedIn: 'root'
})
export class SeguridadService {
  static readonly MARGEN_PROACTIVO_MINUTOS = 2;

  private _http = inject(HttpClient);
  private _jwtHelper: JwtHelperService;

  private _refreshObservable: Observable<string | null> | null = null;

  private _usuarioLogueado: any;
  get usuarioLogueado() {
    if (!this._usuarioLogueado) {
      this._usuarioLogueado = this._jwtHelper.decodeToken(
        this.obtenerAccessToken()?.toString() ?? ''
      );
    }
    return this._usuarioLogueado;
  }
  set usuarioLogueado(valor) {
    this._usuarioLogueado = valor;
  }

  private _menus: Array<IMenu> = [];
  get Menus() {
    if (this._menus.length === 0 && !!window.localStorage.getItem('menus')) {
      let itemsGuardados = window.localStorage.getItem('menus');
      this._menus = !!itemsGuardados ? JSON.parse(itemsGuardados) as Array<IMenu> : [];
    }
    return this._menus;
  }
  set Menus(valor) {
    this._menus = valor;
    window.localStorage.setItem('menus', JSON.stringify(valor));
  }

  private _baseApiUrl: string;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const url = inject<string>('BASE_URL' as any);

    this._baseApiUrl = `${url}/api/v1/sistema/seguridad`;
    this._jwtHelper = new JwtHelperService();
    const sincronizarSesion = (event: StorageEvent) => {
      if (event.key === 'access_token' && event.oldValue !== event.newValue) window.location.reload();
    };
    window.addEventListener('storage', sincronizarSesion);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('storage', sincronizarSesion));

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible'
        && this.obtenerAccessToken()
        && this.tokenExpiraProximamente()) {
        this.refrescarTokenSiNecesario().subscribe();
      }
    });
  }

  public login(loginRequest: ILoginRequest): Observable<IHttpResponse<ITokenResponse>> {
    return this._http.post<IHttpResponse<ITokenResponse>>(
      `${this._baseApiUrl}/token`,
      { ...loginRequest, aplicacionId: environment.aplicacionId }
    );
  }

  public refreshToken(usuarioId: number, refreshToken: string): Observable<IHttpResponse<ITokenResponse>> {
    return this._http.post<IHttpResponse<ITokenResponse>>(
      `${this._baseApiUrl}/refreshToken`,
      { usuarioId, refreshToken, aplicacionId: environment.aplicacionId }
    );
  }

  public logout(): Observable<IHttpResponse<object>> {
    return this._http.post<IHttpResponse<object>>(`${this._baseApiUrl}/logout`, null);
  }

  public procesarToken(tokenResponse: ITokenResponse) {
    window.localStorage.setItem('access_token', tokenResponse.access_token);
    window.localStorage.setItem('refresh_token', tokenResponse.refresh_token);

    this._usuarioLogueado = this._jwtHelper.decodeToken(tokenResponse.access_token);
  }

  public asignarCuenta(cuenta: ICuenta): void {
    window.localStorage.setItem('cuentaId', cuenta.id.toString());
    window.localStorage.setItem('cuentaNombre', cuenta.nombre);
  }

  public obtenerCuenta(): number {
    var cuentaStorage = window.localStorage.getItem('cuentaId');
    return !!cuentaStorage ? +cuentaStorage : +this.usuarioLogueado.cuentaId;
  }

  public obtenerNombreCuenta(): string {
    var nombreCuentaStorage = window.localStorage.getItem('cuentaNombre');
    return !!nombreCuentaStorage ? nombreCuentaStorage : this.usuarioLogueado.cuentaNombre;
  }

  public obtenerUsuarioId(): string {
    return this.usuarioLogueado.sub;
  }

  public obtenerNombreUsuario(): string {
    return this.usuarioLogueado.unique_name;
  }

  public obtenerRol(): string {
    return this.usuarioLogueado.rolId;
  }

  public obtenerNombreRol(): string {
    return this.usuarioLogueado.role;
  }

  public obtenerAccessToken(): string | null {
    return window.localStorage.getItem('access_token');
  }

  public obtenerRefreshToken(): string | null {
    return window.localStorage.getItem('refresh_token');
  }

  public esUsuarioImpersonado(): boolean {
    return this.usuarioLogueado?.impersonado === 'true';
  }

  public impersonar(usuarioId: number): Observable<IHttpResponse<IMenu[]>> {
    let iniciada = false;
    const url = this._baseApiUrl.replace(/\/seguridad$/, '');
    return this._http.post<IHttpResponse<ITokenResponse>>(
      `${url}/Cuentas/${this.obtenerCuenta()}/Usuarios/${usuarioId}/Impersonar`, null
    ).pipe(
      tap(response => { this.iniciarImpersonacion(response.result); iniciada = true; }),
      switchMap(() => this._http.get<IHttpResponse<IMenu[]>>(`${url}/Usuarios/${usuarioId}/Menus`)),
      tap(response => { this.Menus = response.result; }),
      catchError(error => {
        if (iniciada) this.finalizarImpersonacion();
        return throwError(() => error);
      })
    );
  }

  public iniciarImpersonacion(token: ITokenResponse): void {
    if (this.esUsuarioImpersonado()) throw new Error('Ya hay una sesión impersonada');
    const claves = ['access_token', 'refresh_token', 'cuentaId', 'cuentaNombre', 'menus'];
    const original = Object.fromEntries(claves.map(clave => [clave, localStorage.getItem(clave)]));
    localStorage.setItem('sesion_original', JSON.stringify(original));
    localStorage.removeItem('cuentaId');
    localStorage.removeItem('cuentaNombre');
    this.Menus = [];
    this.procesarToken(token);
  }

  public finalizarImpersonacion(): boolean {
    const guardada = localStorage.getItem('sesion_original');
    if (!guardada) return false;
    const original = JSON.parse(guardada) as Record<string, string | null>;
    for (const clave of ['access_token', 'refresh_token', 'cuentaId', 'cuentaNombre', 'menus']) {
      const valor = original[clave];
      if (valor != null) localStorage.setItem(clave, valor);
      else localStorage.removeItem(clave);
    }
    localStorage.removeItem('sesion_original');
    this._usuarioLogueado = null;
    this._menus = [];
    return true;
  }

  public regresarAlUsuarioOriginal(): void {
    if (!this.finalizarImpersonacion()) {
      this.limpiarSesion();
      window.location.assign('/main');
      return;
    }
    const sesion = this.esSesionActiva() ? of(this.obtenerAccessToken()) : this.refrescarTokenSiNecesario();
    sesion.subscribe(token => {
      if (!token) this.limpiarSesion();
      window.location.assign(token ? '/configuracion/cuenta' : '/main');
    });
  }

  public limpiarSesion() {
    this._usuarioLogueado = null;
    this._menus = [];
    window.localStorage.clear();
  }

  public esSesionActiva(): boolean {
    let accessToken = this.obtenerAccessToken();
    if (!accessToken) {
      return false;
    }

    try {
      return !this._jwtHelper.isTokenExpired(accessToken);
    } catch (error) {
      console.error('Error al validar token:', error);
      return false;
    }
  }

  public tokenExpiraProximamente(): boolean {
    const token = this.obtenerAccessToken();
    if (!token) return false;
    try {
      return this._jwtHelper.isTokenExpired(token, SeguridadService.MARGEN_PROACTIVO_MINUTOS * 60);
    } catch {
      return true;
    }
  }

  public refrescarTokenSiNecesario(): Observable<string | null> {
    if (this.esUsuarioImpersonado()) return of(this.esSesionActiva() ? this.obtenerAccessToken() : null);
    if (this._refreshObservable) {
      return this._refreshObservable;
    }

    const accessToken = this.obtenerAccessToken();
    const refreshToken = this.obtenerRefreshToken();

    if (!accessToken || !refreshToken) {
      return of(null);
    }

    const tokenDecodificado = this._jwtHelper.decodeToken(accessToken);
    const usuarioId = +(tokenDecodificado?.sub ?? 0);

    if (!usuarioId) {
      return of(null);
    }

    this._refreshObservable = this.refreshToken(usuarioId, refreshToken).pipe(
      map(response => {
        this.procesarToken(response.result);
        return response.result.access_token;
      }),
      catchError(() => of(null)),
      finalize(() => { this._refreshObservable = null; }),
      shareReplay(1)
    );

    return this._refreshObservable;
  }
}
